import { useCallback, useRef } from "react";
import { CONVERSATION_MESSAGE_PAGE_SIZE, type Attachment } from "@lumen/shared";
import { api } from "../../api";
import { applyQueueSnapshot } from "./conversationQueueState";
import { PROJECTS_CHANGED_EVENT } from "../../components/projects/projectEvents";
import type { DisplayConversation, DisplayMessage } from "./ChatPage";
import type { ComposerOptions } from "../../components/composer/Composer";
import {
  applyConversationStreamEvent,
  delegatedActivityModelIdsFrom,
  isAbortError,
  reconnectConversationStream,
  StreamDisconnectedError,
  streamRequest,
} from "./ChatPage.utils";
import type { ChatActionsContext } from "./ChatContextTypes";
import { useRegenerateAction } from "./useRegenerateAction";
import { useSubagentContinueAction } from "./useSubagentContinueAction";
import {
  preserveConversationTitle,
  rememberConversationTitle,
} from "../../conversationTitles";

const liveGenerationDisconnectedMessage =
  "Live updates are temporarily unavailable. The generation continues in the background.";

function actionError(reason: unknown, fallback: string) {
  return reason instanceof StreamDisconnectedError
    ? liveGenerationDisconnectedMessage
    : reason instanceof Error
      ? reason.message
      : fallback;
}

export function useChatActions(context: ChatActionsContext) {
  const {
    activeRequest,
    activityModelIds,
    approvals,
    conversationRef,
    draftProjectId,
    id,
    modelId,
    navigate,
    onAttention,
    onPrepareAttention,
    refreshConversations,
    setApprovals,
    setConversation,
    setDecidingApprovalIds,
    setError,
    setSending,
    setUsageRevision,
    sending,
    stickToBottom,
    temporary,
    thinkingLevel,
    loadApprovals,
    updateConversation,
    applyConversationSnapshot,
    mergeLatestMessages,
    generationMessageId,
  } = context;
  const questionRefreshRevision = useRef(0);
  const send = useCallback(
    async (
      content: string,
      attachments: Attachment[],
      options: ComposerOptions,
      steer = false,
    ) => {
      if (sending && conversationRef.current) {
        const targetId = conversationRef.current.id;
        try {
          const queued = await api<{ queue: unknown[]; revision: number }>(
            `/conversations/${targetId}/queue`,
            {
              method: "POST",
              body: JSON.stringify({
                content,
                attachmentIds: attachments.map((attachment) => attachment.id),
                attachments,
                options: {
                  ...options,
                  generationParameters: {
                    ...options.generationParameters,
                    activityModelIds: delegatedActivityModelIdsFrom(
                      activityModelIds.current,
                    ),
                  },
                },
                steer,
              }),
            },
          );
          setConversation((current) =>
            applyQueueSnapshot(
              current,
              targetId,
              queued.queue,
              queued.revision,
            ),
          );
        } catch (reason) {
          if (conversationRef.current?.id === targetId)
            setError(actionError(reason, "Could not queue message"));
          throw reason;
        }
        return;
      }
      onPrepareAttention?.();
      const controller = new AbortController();
      activeRequest.current = controller;
      let streamConversationId = "";
      setSending(true);
      setError("");
      stickToBottom.current = true;
      try {
        let target = conversationRef.current;
        if (!target) {
          const conversationInput: {
            modelId?: string;
            agentId?: string;
            temporary: boolean;
            surface?: "chat" | "project";
            groupId?: string;
          } = { temporary };
          // The composer can be submitted before the initial model/agent
          // requests finish. Let the API resolve its configured defaults in
          // that case instead of sending an invalid empty UUID.
          if (modelId) conversationInput.modelId = modelId;
          if (options.agentId) conversationInput.agentId = options.agentId;
          // Project drafts only materialize as conversations once a message
          // is actually sent, mirroring the Chat view's home route.
          if (draftProjectId) {
            conversationInput.surface = "project";
            conversationInput.groupId = draftProjectId;
          }
          target = await api<DisplayConversation>("/conversations", {
            method: "POST",
            body: JSON.stringify(conversationInput),
            signal: controller.signal,
          });
          // Record the prompt title before navigation or sidebar refreshes
          // can observe the newly created conversation's placeholder.
          target = preserveConversationTitle(target);
          if (target.title === "New conversation")
            target = {
              ...target,
              title: content.trim().slice(0, 60) || "Image conversation",
            };
          rememberConversationTitle(target.id, target.title);
          conversationRef.current = target;
          setConversation(target);
          if (!temporary) {
            navigate(
              draftProjectId ? `/projects/c/${target.id}` : `/c/${target.id}`,
              { replace: true },
            );
            // Surface the new conversation in the sidebar immediately. The
            // stream below refreshes it again once the answer finishes, but
            // the list must not depend on the stream completing.
            void refreshConversations();
            if (draftProjectId)
              window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
          }
        }
        const targetId = target.id;
        if (!target.modelId && modelId) {
          target = await api<DisplayConversation>(
            `/conversations/${targetId}`,
            {
              method: "PATCH",
              body: JSON.stringify({ modelId }),
              signal: controller.signal,
            },
          );
          target = preserveConversationTitle(target);
          conversationRef.current = target;
          setConversation(target);
        }
        const requestOptions: ComposerOptions = {
          ...options,
          generationParameters: {
            ...options.generationParameters,
            activityModelIds: delegatedActivityModelIdsFrom(
              activityModelIds.current,
            ),
          },
        };
        if (!id || temporary) {
          const repositoryKey = "lumen:conversation-repository:" + targetId;
          if (requestOptions.repositoryId)
            localStorage.setItem(repositoryKey, requestOptions.repositoryId);
          else localStorage.removeItem(repositoryKey);
        }
        const streamOptions =
          !modelId &&
          requestOptions.generationParameters?.activity === "ask" &&
          !requestOptions.agentId &&
          target.agentId
            ? { ...requestOptions, agentId: target.agentId }
            : requestOptions;
        streamConversationId = targetId;
        const optimisticId = `optimistic-${crypto.randomUUID()}`;
        const optimistic: DisplayMessage = {
          id: optimisticId,
          conversationId: targetId,
          parentId: target.messages?.at(-1)?.id ?? null,
          versionGroupId: optimisticId,
          versionIndex: 1,
          role: "user",
          content,
          attachments,
          toolIds: requestOptions.toolIds,
          artifactIds: requestOptions.artifactIds,
          skillIds: requestOptions.skillIds,
          mcpServerIds: requestOptions.mcpServerIds,
          mcpToolSelections: requestOptions.mcpToolSelections,
          agentWorkspaceId: requestOptions.agentWorkspaceId,
          agentWorkspaceIds: requestOptions.agentWorkspaceIds,
          repositoryIds: requestOptions.repositoryIds,
          agentId: requestOptions.agentId,
          generationParameters: requestOptions.generationParameters,
          createdAt: new Date().toISOString(),
        };
        setConversation((current) =>
          current?.id === targetId
            ? {
                ...current,
                messages: [...(current.messages ?? []), optimistic],
              }
            : current,
        );
        if (!temporary) void refreshConversations();
        await streamRequest(
          `/conversations/${targetId}/messages/stream`,
          {
            content,
            attachmentIds: attachments.map((attachment) => attachment.id),
            ...streamOptions,
          },
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation,
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              mergeLatestMessages,
              conversationId: targetId,
              optimisticMessageId: optimistic.id,
            });
            if (event.type === "user" || event.type === "assistant")
              void refreshConversations();
            if (event.type === "tool") {
              if (event.event.status === "awaiting-approval")
                void loadApprovals();
            }
            if (event.type === "subagent") {
              if (event.run.status === "awaiting-approval")
                void loadApprovals();
            }
            if (event.type === "done") {
              setUsageRevision((revision) => revision + 1);
              void loadApprovals();
            }
            if (event.type === "error") {
              throw new Error(event.error);
            }
          },
          controller.signal,
          {
            isStale: () => conversationRef.current?.id !== targetId,
            idempotencyKey: crypto.randomUUID(),
            onAttention,
            onDegraded: () =>
              setError(
                "Live updates are temporarily unavailable. The generation continues in the background.",
              ),
            onRecovered: () => setError(""),
            notifyOnDone: true,
            reconnect: (assistantId) =>
              reconnectConversationStream(targetId, assistantId),
          },
        );
        if (!temporary) await refreshConversations();
      } catch (reason) {
        if (isAbortError(reason)) return;
        const message = actionError(reason, "Could not send message");
        setError(message);
        throw reason;
      } finally {
        if (activeRequest.current === controller) activeRequest.current = null;
        if (conversationRef.current?.id === streamConversationId)
          setSending(false);
      }
    },
    [
      applyConversationSnapshot,
      loadApprovals,
      draftProjectId,
      id,
      modelId,
      navigate,
      sending,
      refreshConversations,
      temporary,
      onAttention,
      onPrepareAttention,
      updateConversation,
      activeRequest,
      mergeLatestMessages,
      activityModelIds,
      conversationRef,
      generationMessageId,
      setConversation,
      setError,
      setSending,
      setUsageRevision,
      stickToBottom,
    ],
  );
  const stopGeneration = () => {
    const current = conversationRef.current;
    if (current)
      void api<DisplayConversation>(`/conversations/${current.id}/cancel`, {
        method: "POST",
      })
        .then((latest) =>
          setConversation((visible) =>
            visible?.id === latest.id
              ? mergeLatestMessages(visible, latest)
              : latest,
          ),
        )
        .catch(() => undefined);
    activeRequest.current?.abort();
  };
  const continueResponse = useCallback(
    async (message: DisplayMessage) => {
      const current = conversationRef.current;
      if (!current) return;
      setSending(true);
      setError("");
      stickToBottom.current = true;
      const controller = new AbortController();
      activeRequest.current = controller;
      generationMessageId.current = message.id;
      try {
        await streamRequest(
          `/conversations/${current.id}/messages/${message.id}/continue/stream`,
          {
            ...(modelId ? { modelId } : {}),
            ...(thinkingLevel ? { thinkingLevel } : {}),
          },
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation,
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              conversationId: current.id,
            });
            if (event.type === "done") {
              setUsageRevision((revision) => revision + 1);
              void loadApprovals();
            }
            if (event.type === "error") {
              throw new Error(event.error);
            }
          },
          controller.signal,
          {
            isStale: () => conversationRef.current?.id !== current.id,
            onAttention,
            onDegraded: () =>
              setError(
                "Live updates are temporarily unavailable. The generation continues in the background.",
              ),
            onRecovered: () => setError(""),
            notifyOnDone: true,
            reconnect: (assistantId) =>
              reconnectConversationStream(
                current.id,
                assistantId ?? message.id,
              ),
          },
        );
        await Promise.all([loadApprovals(), refreshConversations()]);
      } catch (reason) {
        if (isAbortError(reason)) return;
        setError(actionError(reason, "Could not continue the response"));
      } finally {
        if (activeRequest.current === controller) activeRequest.current = null;
        if (conversationRef.current?.id === current.id) setSending(false);
      }
    },
    [
      loadApprovals,
      modelId,
      onAttention,
      refreshConversations,
      updateConversation,
      activeRequest,
      applyConversationSnapshot,
      conversationRef,
      generationMessageId,
      setConversation,
      setError,
      setSending,
      thinkingLevel,
      setUsageRevision,
      stickToBottom,
    ],
  );
  const continueSubagent = useSubagentContinueAction(context);
  const answerQuestion = useCallback(
    async (
      message: DisplayMessage,
      questionId: string,
      answers: string[],
      custom: boolean,
    ) => {
      const conversationId =
        conversationRef.current?.id ?? message.conversationId;
      if (!conversationId) return;
      let result: {
        alreadyAnswered?: boolean;
        continuationQueued?: boolean;
        waitingForQuestions?: boolean;
      };
      try {
        result = await api<{
          alreadyAnswered?: boolean;
          continuationQueued?: boolean;
          waitingForQuestions?: boolean;
        }>(
          `/conversations/${conversationId}/tool-questions/${questionId}/answer`,
          {
            method: "POST",
            body: JSON.stringify({ answers, custom }),
          },
        );
      } catch (reason) {
        setError(
          reason instanceof Error
            ? reason.message
            : "Could not submit the answer",
        );
        return;
      }
      // Each answer can leave other questions open. Refresh the canonical
      // conversation so answered cards and remaining prompts stay in sync.
      // Revision checks prevent an older concurrent refresh from replacing
      // the snapshot returned for a later answer.
      const refreshRevision = ++questionRefreshRevision.current;
      try {
        const latest = await api<DisplayConversation>(
          `/conversations/${conversationId}?limit=${CONVERSATION_MESSAGE_PAGE_SIZE}`,
        );
        if (
          refreshRevision === questionRefreshRevision.current &&
          conversationRef.current?.id === conversationId
        ) {
          setConversation((current) =>
            current?.id === conversationId
              ? mergeLatestMessages(current, latest)
              : current,
          );
        }
      } catch (reason) {
        setError(
          reason instanceof Error
            ? reason.message
            : "Could not refresh the conversation",
        );
      }
      void refreshConversations();

      // The server queues a single continuation when the final open
      // question is answered. Follow it only for that answer; a sibling
      // answer must leave the other prompts interactive.
      if (
        result.continuationQueued &&
        !result.waitingForQuestions &&
        conversationRef.current?.id === conversationId &&
        !activeRequest.current
      )
        await continueResponse(message);
    },
    [
      activeRequest,
      continueResponse,
      conversationRef,
      mergeLatestMessages,
      refreshConversations,
      setConversation,
      setError,
    ],
  );
  const decideApproval = useCallback(
    async (
      approvalId: string,
      decision: "approved" | "denied",
      message?: string,
      options?: {
        allowScope?: "once" | "exact" | "pattern";
        pattern?: string;
        stop?: boolean;
      },
    ) => {
      const approval = approvals.find((item) => item.id === approvalId);
      // The approval card is rendered from the conversation stream, while the
      // separate approvals list is loaded asynchronously. Do not make that
      // list a prerequisite for deciding an approval: a freshly streamed
      // approval can legitimately be missing from it for a short time.
      const approvalConversationId =
        approval?.conversationId ?? conversationRef.current?.id;
      if (!approvalConversationId) return;
      setDecidingApprovalIds((current) => new Set(current).add(approvalId));
      setApprovals((current) =>
        current.map((approval) =>
          approval.id === approvalId
            ? {
                ...approval,
                status: decision,
                decidedAt: new Date().toISOString(),
              }
            : approval,
        ),
      );
      setError("");
      stickToBottom.current = true;
      let generationStarted = Boolean(options?.stop);
      const controller = new AbortController();
      activeRequest.current = controller;
      try {
        await streamRequest(
          `/agent-workspace-approvals/${approvalId}/decision/stream`,
          { decision, message, ...options },
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation,
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              mergeLatestMessages,
              conversationId: approvalConversationId,
            });
            if (
              (event.type === "sync" || event.type === "assistant") &&
              event.message.role === "assistant"
            ) {
              generationStarted = true;
              setSending(true);
            }
            if (event.type === "tool") {
              if (event.event.status === "awaiting-approval")
                void loadApprovals();
            }
            if (event.type === "subagent") {
              if (event.run.status === "awaiting-approval")
                void loadApprovals();
            }
            if (event.type === "approval-waiting") void loadApprovals();
            if (event.type === "done") {
              setUsageRevision((revision) => revision + 1);
              void loadApprovals();
            }
            if (event.type === "error") {
              throw new Error(event.error);
            }
          },
          controller.signal,
          {
            isStale: () =>
              conversationRef.current?.id !== approvalConversationId,
            onAttention,
            onDegraded: () =>
              setError(
                "Live updates are temporarily unavailable. The generation continues in the background.",
              ),
            onRecovered: () => setError(""),
            notifyOnDone: true,
            stopOnApprovalWaiting: true,
            reconnect: (streamAssistantId) =>
              reconnectConversationStream(
                approvalConversationId,
                streamAssistantId ??
                  generationMessageId.current ??
                  approval?.assistantMessageId,
              ),
          },
        );
        await Promise.all([loadApprovals(), refreshConversations()]);
      } catch (reason) {
        if (isAbortError(reason)) return;
        // A decision can race with another tab or with a disconnected stream.
        // Always replace the local snapshot after a failed decision so stale
        // approval controls cannot remain actionable.
        void api<DisplayConversation>(
          `/conversations/${approvalConversationId}?limit=50`,
        )
          .then((latest) =>
            setConversation((current) =>
              current?.id === latest.id
                ? mergeLatestMessages(current, latest)
                : latest,
            ),
          )
          .catch(() => undefined);
        void loadApprovals();
        const detail = actionError(reason, "Could not continue the response");
        if (detail.includes("response was recovered")) setError("");
        else setError(detail);
      } finally {
        if (activeRequest.current === controller) activeRequest.current = null;
        setDecidingApprovalIds((current) => {
          const next = new Set(current);
          next.delete(approvalId);
          return next;
        });
        if (
          generationStarted &&
          conversationRef.current?.id === approvalConversationId
        )
          setSending(false);
      }
    },
    [
      applyConversationSnapshot,
      approvals,
      loadApprovals,
      onAttention,
      refreshConversations,
      updateConversation,
      activeRequest,
      conversationRef,
      generationMessageId,
      mergeLatestMessages,
      setApprovals,
      setConversation,
      setDecidingApprovalIds,
      setError,
      setSending,
      setUsageRevision,
      stickToBottom,
    ],
  );
  const edit = useCallback(
    async (
      message: DisplayMessage,
      content: string,
      attachments: Attachment[],
      options: ComposerOptions,
    ) => {
      const current = conversationRef.current;
      if (!current) return;
      setSending(true);
      const controller = new AbortController();
      activeRequest.current = controller;
      setError("");
      stickToBottom.current = true;
      try {
        const editGenerationParameters = {
          ...options.generationParameters,
          activityModelIds: delegatedActivityModelIdsFrom({
            ...activityModelIds.current,
            ...(options.generationParameters?.activityModelIds ?? {}),
          }),
        };
        await streamRequest(
          `/conversations/${current.id}/messages/${message.id}/edit/stream`,
          {
            content,
            attachmentIds: attachments.map((attachment) => attachment.id),
            ...options,
            generationParameters: editGenerationParameters,
            ...(modelId ? { modelId } : {}),
          },
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation,
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              conversationId: current.id,
            });
            if (event.type === "tool") {
              if (event.event.status === "awaiting-approval")
                void loadApprovals();
            }
            if (event.type === "subagent") {
              if (event.run.status === "awaiting-approval")
                void loadApprovals();
            }
            if (event.type === "done") {
              setUsageRevision((revision) => revision + 1);
              void loadApprovals();
            }
            if (event.type === "error") {
              throw new Error(event.error);
            }
          },
          controller.signal,
          {
            isStale: () => conversationRef.current?.id !== current.id,
            onAttention,
            onDegraded: () =>
              setError(
                "Live updates are temporarily unavailable. The generation continues in the background.",
              ),
            onRecovered: () => setError(""),
            notifyOnDone: true,
            reconnect: (assistantId) =>
              reconnectConversationStream(current.id, assistantId),
          },
        );
        await refreshConversations();
      } catch (reason) {
        if (isAbortError(reason)) return;
        setError(actionError(reason, "Could not edit message"));
        throw reason;
      } finally {
        if (activeRequest.current === controller) activeRequest.current = null;
        if (conversationRef.current?.id === current.id) setSending(false);
      }
    },
    [
      activeRequest,
      activityModelIds,
      applyConversationSnapshot,
      conversationRef,
      generationMessageId,
      loadApprovals,
      modelId,
      onAttention,
      refreshConversations,
      setConversation,
      setError,
      setSending,
      setUsageRevision,
      stickToBottom,
      updateConversation,
    ],
  );
  const regenerate = useRegenerateAction(context);
  return {
    send,
    stopGeneration,
    continueResponse,
    continueSubagent,
    answerQuestion,
    decideApproval,
    edit,
    regenerate,
  };
}
